import React, { FC, useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Input from "shared/Input/Input";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import useFetch from "hooks/useFetch";
import config from "config.json";
import ErrorModal from "components/Modal/errorModal";

export interface PageLoginProps {
  className?: string;
}

// Modal específico para activar el teléfono
const ActivatePhoneModal: FC<{ isModalOpen: boolean; handleModalClose: () => void; userId: string | null }> = ({ isModalOpen, handleModalClose, userId }) => {
  if (!isModalOpen || !userId) return null;

  const handleActivatePhone = () => {
    window.location.href = `/activate-phone?user=${userId}`;
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="absolute inset-0 bg-black opacity-50"></div>
      <div className="bg-white p-6 rounded-lg shadow-lg z-10">
        <h2 className="text-xl font-bold mb-4">Validación de Teléfono</h2>
        <p>Necesitamos validar tu telefono para poder ingresar. Hace click en el botón para hacer validación.</p>
          <div className="flex justify-between px-4">
          <button
          className="mt-4 bg-gray-500 text-white rounded-full px-4 py-2 "
          onClick={handleModalClose}
        >
          Cerrar
        </button>
        <button
          className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-full"
          onClick={handleActivatePhone}
        >
          Validar Teléfono
        </button>
          </div>
       
      </div>
    </div>
  );
};

const PageLogin: FC<PageLoginProps> = ({ className = "" }) => {
  const url = config.backend_url;
  const location = useLocation();
  const [formData, setFormData] = useState({ mail: "", password: "" });
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isActivatePhoneModalOpen, setIsActivatePhoneModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [userId, setUserId] = useState<string | null>(null);
  const [validationError, setValidationError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const originFromUrl = decodeURIComponent(queryParams.get("origin") || "");

  const { data, error, isLoading, fetchData } = useFetch(
    "POST",
    `${url}/authe/login`,
    formData,
    { "Content-Type": "application/json" }
  );

  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setValidationError("");
  };

  const handleCloseModal = () => {
    setIsErrorModalOpen(false);
    setIsActivatePhoneModalOpen(false);
  };

  const validateForm = () => {
    const { mail, password } = formData;

    if (!mail || !password) {
      setValidationError("Todos los campos son obligatorios.");
      return false;
    }
    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsErrorModalOpen(false);
    await fetchData();
  };

  useEffect(() => {
    const checkPhoneActivation = async (token: string) => {
      try {
        const response = await fetch(`${url}/authe/token`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Setiaauthorization": `${token}`,
          },
        });

        if (!response.ok) {
          setErrorMessage("Error al realizar la petición. Intente más tarde.");
          setIsErrorModalOpen(true);
          return;
        }

        const userData = await response.json();

        if (userData.code && userData.code !== "200") {
          setErrorMessage("Error al realizar la petición. Intente más tarde.");
          setIsErrorModalOpen(true);
          return;
        }

        // Guardar el id del usuario en sessionStorage
        sessionStorage.setItem("user_id", userData.id);
        sessionStorage.setItem("semaphore", userData.semaphore_status);
        setUserId(userData.id);

        if (userData.phones && userData.phones.length > 0 && !userData.phones[0].active) {
          setIsActivatePhoneModalOpen(true);
        } else {
          // Solo guardar el token si el teléfono está validado

          sessionStorage.setItem("user_id", userData.id);
          sessionStorage.setItem("user_token", token);
          sessionStorage.setItem("user_login", "true");
          
          // Redirigir según el origen
          if (originFromUrl === "detail") {
            //navigate("/listing-stay-detail");
            window.location.href = "/listing-stay-detail";
          } else {
            //navigate("/");
            window.location.href = "/";
          }
        }
      } catch (error) {
        setErrorMessage("Error al realizar la petición. Intente más tarde.");
        setIsErrorModalOpen(true);
      }
    };

    if (data) {
      const response = data as any;
      switch (response.code) {
        case "200":
          // Verifica si el teléfono está activado y obtiene el ID del usuario
          checkPhoneActivation(response.message);
          break;
        default:
          setErrorMessage("No se pudo iniciar sesión, por favor intente de nuevo más tarde");
          setIsErrorModalOpen(true);
          break;
      }
    } else if (error) {
      const response = error as any;
      switch (response.code) {
        case "14":
          setErrorMessage("La contraseña es incorrecta");
          setIsErrorModalOpen(true);
          break;
        case "0":
            setErrorMessage("Usuario bloqueado por favor contacte al administrador");
            setIsErrorModalOpen(true);
            break;
        case "6":
          setErrorMessage("El usuario ingresado no existe");
          setIsErrorModalOpen(true);
          break;
        default:
          setErrorMessage("No se pudo iniciar sesión, por favor intente de nuevo más tarde");
          setIsErrorModalOpen(true);
          break;
      }
    }
    
  }, [data, error, originFromUrl, url, navigate]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className={`nc-PageLogin ${className}`} data-nc-id="PageLogin">
      <ErrorModal
        message={errorMessage}
        handleModalClose={handleCloseModal}
        isModalOpen={isErrorModalOpen}
      />
      <ActivatePhoneModal
        isModalOpen={isActivatePhoneModalOpen}
        handleModalClose={handleCloseModal}
        userId={userId}
      />
      <Helmet>
        <title>Iniciar Sesión - Setia Turismo</title>
      </Helmet>
      <div className="container mb-24 lg:mb-32">
        <h2 className="my-10 flex items-center text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
          Inicia Sesión
        </h2>
        <div className="max-w-md mx-auto space-y-6">
          <form className="grid grid-cols-1 gap-6" method="post" onSubmit={handleLogin}>
            <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">Email</span>
              <Input
                type="email"
                name="mail"
                placeholder="example@example.com"
                className="mt-1"
                onChange={handleInputChange}
              />
            </label>
            <label className="block">
              <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
                Contraseña
                <Link to="/recover" className="text-sm text-primary-700 dark:text-primary-500">
                  Olvidaste tu contraseña?
                </Link>
              </span>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="mt-1"
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 px-4 text-gray-600"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? "Ocultar" : "Mostrar"}
                </button>
              </div>
            </label>
            {validationError && (
              <div className="text-red-500 text-center text-sm">{validationError}</div>
            )}
            <ButtonPrimary type="submit" disabled={isLoading}>Iniciar Sesión</ButtonPrimary>
          </form>
          <span className="flex text-center justify-center text-neutral-700 dark:text-neutral-300">
            No tenés cuenta?{" "}
            <Link to="/signup">
              <p className="text-primary-700 dark:text-primary-500">&nbsp;Registrate</p>
            </Link>
          </span>
        </div>
      </div>
    </div>
  );
};

export default PageLogin;
